import { getCookie } from 'helpers/cookie';
import { HotjarUser, setHotjarUser } from 'hotjar';
import { IUserDetails } from 'interfaces/user';
import { BehaviorSubject, Observable, of } from 'rxjs';
import * as Rx from 'rxjs';
import * as RxOp from 'rxjs-operators';
import apiService from 'services/api';
import logService from 'services/log';
import { ACCOUNT_ENV, COOKIE_DOMAIN } from 'settings';

import { HyperflowToken, LoginAsEmail, SendHelpRequest, SupportChatToken, User } from './types';

const ONE_YEARS_IN_SECONDS = 60 * 60 * 24 * 365;

export class UserService {
  private user$: BehaviorSubject<User>;
  private logout$: BehaviorSubject<() => void>;

  constructor() {
    this.user$ = new BehaviorSubject(null);
    this.logout$ = new BehaviorSubject(null);
  }

  public getUser(): Observable<User> {
    return this.user$.asObservable();
  }

  public setUser(user: User): Observable<User> {
    this.user$.next(user);
    const { cli_cod: id, user_email: email, user_name: name, belt, acpo_original_user_cli_cod } = user;

    logService.setUser(user);
    setHotjarUser({
      id,
      email,
      name,
      belt,
      isAccessControl: id === acpo_original_user_cli_cod
    } as HotjarUser);

    const userCookie = getCookie('blk_user');

    if (!userCookie || (userCookie && userCookie !== user.id)) {
      document.cookie = `blk_user=${user?.id};domain=${COOKIE_DOMAIN};path=/;max-age=${ONE_YEARS_IN_SECONDS}`;
    }

    return of(user);
  }

  public setLogoutUser(action: () => void): Observable<() => void> {
    this.logout$.next(action);
    return of(action);
  }

  public logout(): void {
    localStorage.clear();
    sessionStorage.clear();
    (window as any).Eduzz.Accounts.logout({
      ...ACCOUNT_ENV,
      redirectTo: window.location.origin
    });
  }

  public setMeAsProducer(data: any): Rx.Observable<any> {
    return apiService.post('/user/set_as_producer', data);
  }

  public hasUsedTour(data: any): Rx.Observable<any> {
    return apiService.put('/user/tour', data);
  }

  public loginByEventHash(data: any): Rx.Observable<any> {
    return apiService.post('/auth/loginbyeventhash', data).pipe(RxOp.map(res => res.data[0]));
  }

  public loginAs(data: any): Rx.Observable<User> {
    return apiService.post('/user/login_as', data).pipe(RxOp.map(res => res.data[0]));
  }

  public sendHelp(helpData: SendHelpRequest): Rx.Observable<any> {
    const { eventId, ...data } = helpData;
    return apiService.post(`help_services/helpsend/${eventId}`, data);
  }

  public loginAsEmail(email: string): Rx.Observable<LoginAsEmail> {
    return apiService.post('/user/acpo_login_as', { email }).pipe(RxOp.map(res => res.data[0]));
  }

  public getDetails(): Rx.Observable<IUserDetails> {
    return apiService.get('/user/details').pipe(RxOp.map(res => res.data));
  }

  public createSupportChatToken(ssid): Rx.Observable<SupportChatToken> {
    return apiService.post('/user/support-chat/create-token', { ssid });
  }

  public createHyperflowToken(userToHyperflow): Rx.Observable<HyperflowToken> {
    return apiService.post('/user/support-chat/create-hyperflow-token', { userToHyperflow });
  }

  public getBeltById(id: number): string {
    const belts = {
      100: 'Red Belt',
      101: 'Orange Belt',
      102: 'Green Belt',
      103: 'Black Belt',
      104: 'Golden Belt',
      109: 'Black Belt D2',
      110: 'Black Belt D3',
      111: 'Black Belt D4',
      112: 'Black Belt D5',
      113: 'Black Belt D6',
      114: 'Black Belt D7',
      115: 'Black Belt D8',
      116: 'Black Belt D9',
      300: 'Sensei',
      302: 'Golden Belt D20',
      303: 'Golden Belt D30',
      304: 'Golden Belt D40',
      305: 'Golden Belt D50',
      306: 'Golden Belt D60',
      307: 'Golden Belt D70',
      308: 'Golden Belt D80',
      309: 'Golden Belt D90'
    };

    return belts[id] || null;
  }
}

const userService = new UserService();
export default userService;
